import { api } from '@/lib/api';
import type {
  CreateSupplierPayload,
  Supplier,
  SupplierListFilters,
  UpdateSupplierPayload,
} from './types';

/**
 * Suppliers REST client (L3 backend contract).
 *
 *   GET    /suppliers?scope=&companyId=&q=&active=
 *   GET    /suppliers/:id
 *   POST   /suppliers
 *   PATCH  /suppliers/:id
 *   DELETE /suppliers/:id
 *   POST   /suppliers/:id/scopes
 *   DELETE /suppliers/:id/scopes/:companyId
 */
export const suppliersApi = {
  list: async (filters: SupplierListFilters = {}): Promise<Supplier[]> => {
    const { data } = await api.get<Supplier[]>('/suppliers', {
      params: {
        ...(filters.scope && { scope: filters.scope }),
        ...(filters.companyId && { companyId: filters.companyId }),
        ...(filters.q && { q: filters.q }),
        ...(filters.active && { active: filters.active }),
      },
    });
    return data;
  },

  getById: async (id: string): Promise<Supplier> => {
    const { data } = await api.get<Supplier>(`/suppliers/${id}`);
    return data;
  },

  create: async (input: CreateSupplierPayload): Promise<Supplier> => {
    const { data } = await api.post<Supplier>('/suppliers', input);
    return data;
  },

  update: async (
    id: string,
    patch: UpdateSupplierPayload,
  ): Promise<Supplier> => {
    const { data } = await api.patch<Supplier>(`/suppliers/${id}`, patch);
    return data;
  },

  remove: async (id: string): Promise<void> => {
    await api.delete(`/suppliers/${id}`);
  },

  addScope: async (id: string, companyId: string): Promise<Supplier> => {
    const { data } = await api.post<Supplier>(`/suppliers/${id}/scopes`, {
      companyId,
    });
    return data;
  },

  removeScope: async (id: string, companyId: string): Promise<Supplier> => {
    const { data } = await api.delete<Supplier>(
      `/suppliers/${id}/scopes/${companyId}`,
    );
    return data;
  },
};
